import { z } from 'zod';

export const CustomTemplateApi = z.object({
  id: z.string(),
  name: z.string().describe('The template name'),
  version: z.int().describe('The latest version of the template'),
});
export type CustomTemplateApi = z.infer<typeof CustomTemplateApi>;

// Async-job handle returned by POST /v1/customTemplates/{templateId}/jobs, serialized
// as a `jobs` JSON:API resource so it lines up with GET /v1/jobs/{jobId}.
export const CustomTemplateJobApi = z.object({
  id: z.string(),
  templateId: z.string().describe('Id of the template being generated'),
  status: z.literal('PENDING').describe('Initial job status'),
});
export type CustomTemplateJobApi = z.infer<typeof CustomTemplateJobApi>;

// Job status returned by GET /v1/jobs/{jobId}. The templateId + status prefix matches
// CustomTemplateJobApi (the creation handle) so a caller can hold one shape from kickoff
// through polling; the counts let a slow job be told apart from a stuck one.
export const CustomTemplateJobStatusApi = z.object({
  id: z.string(),
  templateId: z.string().optional().describe('Id of the template being generated'),
  status: z.enum(['PENDING', 'COMPLETED', 'FAILED']).describe('Overall job status'),
  totalRows: z.int().describe('Number of loans in the request'),
  processedRows: z.int().describe('Number of loans processed so far'),
  createdAt: z.iso.datetime().describe('When the job was created'),
});
export type CustomTemplateJobStatusApi = z.infer<typeof CustomTemplateJobStatusApi>;

// One per-loan outcome within a job, returned by GET /v1/jobs/{jobId}/rows[/{rowId}].
export const CustomTemplateJobRowApi = z.object({
  id: z.string(),
  loanId: z.string().optional().describe('The LOS id the row was requested for'),
  status: z.enum(['PENDING', 'COMPLETED', 'FAILED']).describe('Per-row outcome'),
  reason: z
    .string()
    .optional()
    .describe('Failure reason, present only for known validation failures; absent for unexpected errors'),
});
export type CustomTemplateJobRowApi = z.infer<typeof CustomTemplateJobRowApi>;
